import React, { useState } from 'react';
import { Icons } from '../../assets';
import classes from "./style.module.scss";
import InputBox from '../../component/InputBox';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import PinIcon from '@mui/icons-material/Pin';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Button from '../../component/Button';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login, saveAccessToken, saveSessionActive, saveUserData, saveUserPermission } from '../../redux/reducers/authSlice';
import { setAuthToken } from '../../service/helpers';
import { saveLoadingState } from '../../redux/reducers/loadingSlice';
import { Toast } from '../../service/toast';

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState('');
  const [invalidPassword, setInvalidPassword] = useState('')
  const [apiError, setApiError] = useState({});
  const [showOtpField, setShowOtpField] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState({});
  const passwordError = "Invalid password, please try again"

  const dispatch = useDispatch();

  const formMethods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    formState: { errors },
    register,
    getValues,
  } = formMethods;

  const togglePassword = () => {
    setShowPassword((prevState) => !prevState);
  }

  const handleBacktoLogin = () => {
    setShowOtpField(false);
    setApiError({});
  }

  const onSubmit = (data) => {
    dispatch(saveLoadingState(true));
    
    // If OTP field is not shown yet, first request the OTP
    if (!showOtpField) {
      const requestData = {
        email: data?.email,
        password: data?.password,
      };
      
      // Store credentials for the second step
      setLoginCredentials(requestData);
      
      login(requestData)
        .then((res) => {
          // Check if the response indicates OTP was sent
          if (res.data.message.includes("OTP has been sent")) {
            setShowOtpField(true);
            Toast({
              type: 'success',
              message: 'OTP has been sent to your email'
            });
          } else {
            // Handle direct login if OTP is not required (fallback)
            handleLoginSuccess(res);
          }
          dispatch(saveLoadingState(false));
        })
        .catch((err) => {
          handleLoginError(err);
        });
    } else {
      // Second step - send credentials with OTP
      const requestData = {
        ...loginCredentials,
        otp: data?.otp
      };
      
      login(requestData)
        .then((res) => {
          handleLoginSuccess(res);
        })
        .catch((err) => {
          handleLoginError(err);
          // Add specific OTP error handling
          if (err.response?.data?.message?.includes("OTP")) {
            setApiError(prev => ({
              ...prev,
              otp: err.response.data.message
            }));
          }
        });
    }
    
    setTimeout(() => {
      dispatch(saveLoadingState(false));
    }, 5000);
  };

  const handleLoginSuccess = (res) => {
    const token = res.data.data.token;
    setAuthToken(token);
    dispatch(saveAccessToken(token));
    dispatch(saveUserData(res.data.data.admin));
    dispatch(saveUserPermission(res.data.data.admin.permissions));
    dispatch(saveSessionActive(res.data.status));
    dispatch(saveLoadingState(false));
    navigate("/admin/dashboard");
  };

  const handleLoginError = (err) => {
    const errorMessage = err.response?.data?.message || "Login failed";
    setApiError({
      email: errorMessage.includes("Details") ? errorMessage : "",
      password: errorMessage.includes("Password") ? passwordError : "",
      otp: errorMessage.includes("OTP") ? errorMessage : ""
    });
    Toast({
      type: 'error',
      message: 'Unable to process the request!..'
    });
    dispatch(saveLoadingState(false));
  };

  const handleClick = () => {
    navigate('/auth/forgotPassword');
  };

  const getButtonText = () => {
    if (!showOtpField) {
      return "Login";
    } else {
      return "Verify OTP";
    }
  };

  return (
    <div className={`${classes.mainLogin}`}>
      <div className={`${classes.centerForm}`}>
        <img src={Icons.kgLogo} className={`${classes.mainLogo}`} alt="logo" />
        {showOtpField 
         ?  <p className={`${classes.welcomeText}`}>Enter the OTP sent to your email</p> 
         :  <p className={`${classes.welcomeText}`}>Welcome Back. Please login to continue</p>
          }
        
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {!showOtpField && (
              <>
              <div className='!mb-6'>
              <InputBox
                label={"Email"}
                icon={<MailOutlinedIcon sx={{ color: "#000000" }} />}
                className={`${classes.emailInput}`}
                name={"email"}
                authInput
                {...register("email", {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                error={errors.email || apiError.email}
                messages={{
                  required: "Email is required",
                  pattern: "Invalid email address",
                  custom: apiError.email
                }}
              />
            </div>

            <div className='!mb-4'>
              <InputBox
                label={"Password"}
                type={showPassword ? "text" : "password"}
                icon={(showPassword) ? <RemoveRedEyeOutlinedIcon sx={{ color: apiError.password ? "red" : "black" }} onClick={togglePassword} /> : <VisibilityOffOutlinedIcon sx={{ color: apiError.password ? "red" : "black" }} onClick={togglePassword} />}
                className={apiError.password ? `${classes.errorPassword}` : `${classes.passwordInput}`}
                name={"password"}
                authInput
                {...register("password", {
                  minLength: {
                    value: 6,
                    message: "Minimum 6 characters required",
                  },
                })}
                error={errors.password || apiError.password}
                messages={{
                  required: "Password is required",
                  minLength: "Minimum 6 characters required",
                  custom: apiError.password
                }}
              />
            </div>
          </>)}
            

            {showOtpField && (
              <div className="!mb-6">
                <InputBox
                  label="OTP"
                  icon={<PinIcon sx={{ color: "#000000" }} />}
                  className={`${classes.emailInput}`}
                  name="otp"
                  authInput
                  {...register("otp", {
                    required: "OTP is required",
                    pattern: {
                      value: /^\d{6}$/,
                      message: "OTP must be exactly 6 digits",
                    },
                  })}
                  error={errors.otp || apiError.otp}
                  messages={{
                    required: "OTP is required",
                    pattern: "Invalid OTP",
                    custom: apiError.otp
                  }}
                />
              </div>
            )}

            <div className={`${classes.forgotDiv}`}>
         {showOtpField 
         ?  <label className={`${classes.forgotPassword}`} onClick={handleBacktoLogin}>Back to Login</label> 
         :  <label className={`${classes.forgotPassword}`} onClick={handleClick}>Forgot Password?</label>
          }     
            </div>
            <Button text={getButtonText()} className={`w-[100%]`} />
          </form>
        </FormProvider>
      </div>
    </div>
  );
}

export default Login;